// Calls the Cloud Functions in functions/index.js, which hold the NVIDIA API
// key server-side via Secret Manager (a direct browser -> NVIDIA fetch was
// tried and confirmed broken by CORS — NVIDIA's API never sends an
// Access-Control-Allow-Origin header, so the browser blocks it before the
// request ever reaches NVIDIA, in dev or prod. A server-side call is the
// only option).
//
// This is a two-stage agentic pipeline, not a single model call:
//   Stage 1 (extractProductInfo) — a vision model reads the screenshot(s)
//     and pulls out every visible {label, value} spec row, verbatim.
//   Stage 2 (mapProductFields)   — a DIFFERENT NVIDIA model, given only the
//     stage-1 text output (no image), independently maps it onto the
//     caller's exact target fields and is instructed to leave a field blank
//     rather than guess. A second model checking the first model's work
//     catches mistakes a single model would just repeat with confidence.
import { getFunctions, httpsCallable } from 'firebase/functions';

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] || '');
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export async function extractProductInfoAI(files) {
  const fileList = Array.isArray(files) ? files : [files];
  if (fileList.length === 0) throw new Error('No screenshots selected.');
  if (fileList.length > 4) throw new Error('Please select at most 4 screenshots.');

  const images = await Promise.all(fileList.map(async (file) => ({
    base64: await fileToBase64(file),
    mimeType: file.type || 'image/jpeg',
  })));

  const functions = getFunctions();
  const call = httpsCallable(functions, 'extractProductInfo');
  const res = await call({ images });
  return res.data; // { name, specs: [{ label, value }] }
}

// targetFields: [{ key: 'Processor', hint: 'CPU/processor details' }, ...]
// Returns { name, fields: { [key]: value }, notes } or throws — callers
// should treat a thrown error as "verification unavailable" and fall back
// to local rule-based mapping rather than blocking the user.
export async function mapProductFieldsAI(name, specs, targetFields) {
  if (!Array.isArray(specs) || specs.length === 0) throw new Error('No specs to verify.');
  if (!Array.isArray(targetFields) || targetFields.length === 0) throw new Error('No target fields given.');

  const functions = getFunctions();
  const call = httpsCallable(functions, 'mapProductFields');
  const res = await call({ name, specs, targetFields });
  return res.data;
}
